import { AxiosInstance } from "axios";
import { AdapterMessage } from "../adapters/types";
import { pickDefined } from "../utils/utils";

export type InputMessageAttachment = {
  name: string;
  mimeType: string;
  content: string;
};

export type InputMessage = {
  type: "human" | "tool" | "interrupt";
  content: string;
  tool_call_id: string | null;
  name: string | null;
  attachments: InputMessageAttachment[] | null;
};

export enum AgentThreadStatus {
  InProgress = "in_progress",
  Error = "error",
  Ready = "ready",
  InputRequired = "input_required",
  Completed = "completed",
  Cancelled = "cancelled",
}

export type AgentThread = {
  id: string;
  name: string;
  agent_id: string;
  assistant_id?: string | null;
  created_at: string;
  is_favorite: boolean;
  last_message_at: string | null;
  last_read_at: string | null;
  status: AgentThreadStatus | null;
};

export type ThreadStatusCountsMap = Partial<Record<AgentThreadStatus, number>>;

export type PaginatedThreads = {
  items: AgentThread[];
  total: number;
  /** Per-status totals; ignore search/favorite/status filters, respect agent. */
  status_counts?: ThreadStatusCountsMap;
};

type AgentThreadResponse = Omit<AgentThread, "agent_id" | "is_favorite"> & {
  agent_id?: string;
  is_favorite?: boolean;
};

type PaginatedThreadsResponse = Omit<PaginatedThreads, "items"> & {
  items: AgentThreadResponse[];
};

export type GetThreadsParams = {
  search?: string;
  isFavorite?: boolean;
  offset?: number;
  limit?: number;
  status?: AgentThreadStatus;
  agentId?: string;
  sortBy?: "last_message_at" | "created_at";
};

export const getThreads = async (
  axiosInstance: AxiosInstance,
  {
    search,
    isFavorite,
    offset,
    limit,
    status,
    agentId,
    sortBy,
  }: GetThreadsParams = {},
  signal?: AbortSignal
): Promise<PaginatedThreads> => {
  const params = pickDefined({
    search,
    is_favorite: isFavorite,
    offset,
    limit,
    status,
    agent_id: agentId,
    sort_by: sortBy,
  });
  const { data } = await axiosInstance.get<PaginatedThreadsResponse>(
    `/agent/thread/`,
    {
      params: Object.keys(params).length ? params : undefined,
      signal,
    }
  );

  return {
    ...data,
    items: data.items.map((thread) => ({
      ...thread,
      agent_id: thread.agent_id ?? "",
      is_favorite: thread.is_favorite ?? false,
    })),
  };
};

export const useChatApi = (axiosInstance: AxiosInstance) => {
  const createAssistantThread = async (
    assistantId: string,
    thread: { user_message?: string; attachment_names?: string[] }
  ) => {
    return (
      await axiosInstance.post<{ id: string }>(
        `/assistant/${assistantId}/threads`,
        thread
      )
    ).data;
  };

  const getThreadHistory = async (
    threadId: string
  ): Promise<AdapterMessage[]> => {
    if (!threadId) {
      return [];
    }

    const messages = (
      await axiosInstance.get(`/agent/thread/${threadId}/history`)
    ).data;

    return messages.map((message: AdapterMessage) => {
      message.attachments = (message.metadata?.attachments ?? []).map(
        (attachment) => ({
          name: attachment.name!,
          type: "file",
          contentType: attachment.mime_type!,
          url: attachment.url,
        })
      );
      return message;
    });
  };

  const getThread = async (id: string): Promise<AgentThread> => {
    return (await axiosInstance.get(`/agent/thread/${id}`)).data;
  };

  const deleteThread = async (threadId: string) => {
    return (await axiosInstance.delete(`/agent/thread/${threadId}`)).data;
  };

  const updateThread = async (
    threadId: string,
    thread: { name?: string; is_favorite?: boolean }
  ) => {
    return (await axiosInstance.put(`/agent/thread/${threadId}`, thread)).data;
  };

  const markThreadAsRead = async (threadId: string) => {
    return (await axiosInstance.post(`/agent/thread/${threadId}/mark-as-read`))
      .data;
  };

  const sendMessageAsync = async (
    threadId: string,
    message: InputMessage
  ): Promise<{ run_id: string }> => {
    return (
      await axiosInstance.post(
        `/agent/thread/${threadId}/send-message`,
        message
      )
    ).data;
  };

  const stopExecution = async (
    threadId: string,
    runId: string
  ): Promise<{ success: boolean }> => {
    return (
      await axiosInstance.post(`/agent/thread/${threadId}/stop`, {
        run_id: runId,
      })
    ).data;
  };

  const getAssistantThreads = async (
    assistantId: string
  ): Promise<AgentThread[]> => {
    return (await axiosInstance.get(`/assistant/${assistantId}/threads`)).data;
  };

  return {
    sendMessageAsync,
    stopExecution,
    getThreadHistory,
    createAssistantThread,
    deleteThread,
    updateThread,
    markThreadAsRead,
    getThread,
    getAssistantThreads,
  };
};
